import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import {
  catchError,
  filter,
  finalize,
  from,
  groupBy,
  map,
  mergeMap,
  of,
  reduce,
  switchMap,
  tap,
  toArray,
} from 'rxjs';
import { DebtSourceEnum } from 'src/app/configuration/enums/company.enum';
import { CreateCompanyService } from 'src/app/configuration/services/create-company.service';
import { ContractAccount } from 'src/app/features/logged-area/consult-account/models/contract-account.model';
import { Invoices } from 'src/app/models/invoice.model';
import { ConsultAccountsService } from 'src/app/services/consult-accounts.service';
import { DebtsApi } from 'src/app/shared/apis/debts.api';
import { GetDebtsResponse, InfoToGetDebts } from 'src/app/shared/models/debts.model';
import { AppState } from '../..';
import { setDebtsError, setLoadingDisabled } from '../config/config.actions';
import { setPartnerId } from '../purchase/purchase.actions';
import {
  fetchContractsAccountsAndDebitsFailure,
  fetchContractsAccountsAndDebitsRequest,
  fetchContractsAccountsAndDebitsSuccess,
  fetchDebtsRequest,
  fetchDebtsRequestFailure,
  fetchDebtsRequestSuccess,
  setContractAccounts,
  setDebtsInfo,
  setGroupedInvoices,
  setInvoices,
  setOrderId,
} from './payment.actions';

@Injectable()
export class PaymentEffects {
  constructor(
    private readonly actions$: Actions,
    private readonly store: Store<AppState>,
    private readonly consultAccountsService: ConsultAccountsService,
    private readonly debtsApi: DebtsApi,
    private readonly companyService: CreateCompanyService
  ) {}

  fetchDebtsRequest$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(fetchDebtsRequest),
      switchMap((action) => {
        return this.consultAccountsService.checkCustomerDebts(action.debts).pipe(
          map((invoices) => {
            return fetchDebtsRequestSuccess({
              debts: invoices,
              debtsInfo: action.debtsInfo,
              root: action.root,
            });
          }),
          catchError(() => of(fetchDebtsRequestFailure()))
        );
      })
    );
  });

  fetchDebtsRequestSuccess$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(fetchDebtsRequestSuccess),
        tap((response) => {
          const onlyUnpaidInvoices = response.debts.invoices.filter(
            (invoice: Invoices) => !invoice.is_paid
          );
          this.store.dispatch(setDebtsError({ error: false }));

          this.store.dispatch(setInvoices({ invoices: onlyUnpaidInvoices }));

          this.store.dispatch(setDebtsInfo({ debts: response.debtsInfo }));

          this.store.dispatch(setOrderId({ orderId: response.root.order?.orderId }));

          this.store.dispatch(setPartnerId({ partnerId: response.root.partnerId }));

          this.store.dispatch(setLoadingDisabled());
        })
      );
    },
    { dispatch: false }
  );

  fetchDebtsRequestFailure$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(fetchDebtsRequestFailure),
        tap(() => {
          const statusError: number = JSON.parse(sessionStorage.getItem('error') || '').status;
          if (this.companyService.debtSourceName === DebtSourceEnum.embasa && statusError == 504)
            this.store.dispatch(setDebtsError({ error: true }));

          this.store.dispatch(setLoadingDisabled());
        })
      );
    },
    { dispatch: false }
  );

  fetchContractsAccountAndDebtsRequest$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(fetchContractsAccountsAndDebitsRequest),
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      switchMap((action: { debts: InfoToGetDebts; document: string; debtsInfo: any }) => {
        return this.debtsApi.getDebts({ payload: action.debts }).pipe(
          finalize(() => this.store.dispatch(setLoadingDisabled())),
          switchMap(({ invoices }: GetDebtsResponse) => {
            const debtsArray = Object.values(invoices);
            return from(debtsArray).pipe(
              filter((invoice) => !invoice.is_paid),
              groupBy((debt: Invoices) => debt.installation || debt.contract_account),
              mergeMap((group) =>
                group.pipe(
                  toArray(),
                  map((groupedDebts) => ({ key: group.key, values: groupedDebts }))
                )
              ),
              reduce(
                (acc, curr) => {
                  acc[curr.key as number] = curr.values;
                  return acc;
                },
                {} as Record<number, Invoices[]>
              ), //LÓGICA PARA AGRUPAR OS INVOICES POR INSTALLATION
              map((groupedDebts) => {
                const contractAccounts: ContractAccount[] = Object.keys(groupedDebts).map(
                  (key) => ({
                    number: key,
                    uc_document: action.document,
                    isSelected: false,
                  })
                );
                return fetchContractsAccountsAndDebitsSuccess({
                  groupedDebts: groupedDebts,
                  contractAccounts: contractAccounts,
                  debtsInfo: action.debtsInfo,
                });
              }),
              catchError(() => {
                return of(fetchContractsAccountsAndDebitsFailure());
              })
            );
          }),
          catchError(() => {
            return of(fetchContractsAccountsAndDebitsFailure());
          })
        );
      })
    );
  });

  fetchContractsAccountsAndDebitsSuccess$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(fetchContractsAccountsAndDebitsSuccess),
        tap(
          (response: {
            groupedDebts: Record<number, Invoices[]>;
            contractAccounts: ContractAccount[];
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            debtsInfo: any;
          }) => {
            this.store.dispatch(setGroupedInvoices({ groupedInvoices: response.groupedDebts }));
            this.store.dispatch(
              setContractAccounts({ contractAccounts: response.contractAccounts })
            );
            this.store.dispatch(setDebtsInfo({ debts: response.debtsInfo }));
            this.store.dispatch(setLoadingDisabled());
          }
        )
      );
    },
    { dispatch: false }
  );

  fetchContractsAccountsAndDebitsFailure$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(fetchContractsAccountsAndDebitsFailure),
        tap(() => {
          this.store.dispatch(setLoadingDisabled());
        })
      );
    },
    { dispatch: false }
  );
}
